/**
 * The tooltip block: extra lines on the hover card, under the category label and the plotted values
 * every chart already prints there. It declares no display type of its own — it is carried by the
 * types whose renderer draws a hover card, named in `carriedBy` below.
 *
 * A hover card is not a template and never a panel: everything it prints is a value on the row the
 * reader is pointing at, formatted by the same formatter a table cell uses. That is the whole
 * abstraction. "Show me who is behind this bar" is a `list` column built in the panel's own SQL
 * (`list(owner_name)[1:5]`), named here — so the row stays small however many records it counts,
 * and no gesture has to fetch anything.
 */
import { ANALYTICS_COLUMN_TYPES, normalizeAnalyticsColumnType } from '../../../cirra/analyticsColumnTypes.js';

const tooltip = {
  /* No names: a block, not a renderer. */
  names: [],
  block: 'tooltip',
  /* The types whose renderer prints these lines today — the cartesian charts, which share one hover
     card. Not `sharedBlock`: a type with no hoverable mark has nowhere to put them. Opening it to
     another type is one id here plus that type's renderer printing them, the same discipline
     `events` and `annotations` follow. */
  carriedBy: ['bar', 'line', 'combo'],
  fields: {
    /* Columns, in the order they are printed, stored as `{ key, type }` entries — the same shape a
       displayed column has, so the type vocabulary and THE formatter are shared rather than
       re-implemented for the hover card. An optional list, so it is allowed to end up empty: that is
       how the extra lines come off. */
    fields: { kind: 'list', keyField: 'key', label: 'Extra fields', item: { type: { kind: 'enum', values: ANALYTICS_COLUMN_TYPES, normalize: normalizeAnalyticsColumnType } } }
  },
  /* Advanced mode only, where the whole config is returned fresh each resolution: code that wants no
     extra lines omits the block, so a block arriving with no fields is a typo rather than an intent —
     the same reading annotations gives an absent labelKey. */
  validate: ({ block }) => ((Array.isArray(block.fields) ? block.fields : []).length > 0 ? '' : 'tooltip needs fields, the columns whose values the hover card also prints — or leave the block out entirely, which is how a panel carries no extra lines.'),
  stateSummary: ({ block }) => {
    const keys = (Array.isArray(block.fields) ? block.fields : []).map((entry) => (typeof entry === 'string' ? entry : entry?.key || '')).filter(Boolean);

    return keys.length > 0 ? `tooltip: also shows ${keys.join(', ')}` : '';
  },
  toolSchema: {
    type: ['object', 'null'],
    additionalProperties: false,
    required: [],
    /* Signed 2026-08-17: asked to show who was behind each point on hover, an agent reached for a
       drill-down instead — the only lever that existed — and wired a table nobody could open by
       hovering. The sentence naming hover as its own answer is what stops that, so it sits here in
       the tool that writes both, not in the mode prompt. */
    description: 'Extra lines on the hover card (bar, line and combo charts), under the category label and the plotted values it already shows. THE answer to "when I hover I want to see which ones these are" — a hover card cannot be clicked into, so reach for this rather than a drill-down, which needs a click and opens a whole view. The values are COLUMNS on the same rows the panel plots, so put the detail in the panel\'s own SQL: aggregate to one row per category and carry a short list beside the count (`count(*) AS sqls, list(owner_name)[1:5] AS top_owners`), capped in the SQL because a row holding thousands of names pays for them on every load. Five items print and the rest are counted.',
    properties: {
      fields: {
        type: 'array',
        description: 'The columns whose values the hover card also prints, in order. Full replacement; an empty list takes the extra lines off.',
        items: {
          type: 'object',
          additionalProperties: false,
          required: ['key'],
          properties: {
            key: { type: 'string', description: 'Column read from the rows the panel plots (e.g. "top_owners"). A column that is not on those rows prints nothing.' },
            type: {
              type: ['string', 'null'],
              enum: [...ANALYTICS_COLUMN_TYPES, null],
              description: `How the value is FORMATTED, from the same vocabulary the displayed columns use: ${ANALYTICS_COLUMN_TYPES.join(', ')}. Declare it whenever the value is not plain text — "list" for a column holding several values, and currency, percent, duration and date each get their own formatting. Emit raw values from SQL and declare the type here rather than formatting them into strings yourself.`
            }
          }
        }
      }
    }
  }
};

export default tooltip;
